import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import NotificationChannelMgmtService from '../../shared/services/NotificationChannelMgmtService';
import {
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Theme,
  Typography,
  WithStyles,
  withStyles
} from '@material-ui/core';
import HttpCallConfig from '../../core/models/HttpCallConfig';
import { INotificationRouteParams } from '../pages/OrganizationNotificationChannelDetail';
import { IsValidHTTPSUrl } from 'src/modules/core/helpers/ValidationHelper';
import AreYouSureModal from 'src/modules/shared/dataSharing/AreYouSureModal';
import NotificationChannelHttp from '../models/NotificationChannelHttp';
import Loading from 'src/modules/shared/components/loading/Loading';
import NotificationChannelTypeId from '../models/NotificationChannelTypeId';
import NotificationChannelHttpMethod from '../models/NotificationChannelHttpMethod';
import StateHelper from 'src/modules/shared/helpers/StateHelper';
import NotificationChannelHttpAuthenticationSettings from "./NotificationChannelHttpAuthenticationSettings";
import EditableList from "../../shared/components/EditableList";
import NotificationChannelHttpAuthenticationType from "../models/NotificationChannelHttpAuthenticationType";
import NotificationChannelHttpAuthenticationConfig from "../models/NotificationChannelHttpAuthenticationConfig";

const styles = (theme: Theme) => ({
  paper: {
    padding: theme.spacing.unit * 3,
  },
  button: {
    marginTop: theme.spacing.unit * 1,
  },
  dropDown: {
    width: 108,
  },
  textField: {
    width: 240,
  },
  editableList: {
    marginTop: 40,
    marginBottom: 40
  }
});

const notificationChannelsOverviewPath: string =  '/organization/notificationchannels';

interface IValidation {
  'notificationChannel.name'?: string;
  'notificationChannel.address'?: string;
  'notificationChannel.authenticationConfig.tokenEndpoint'?: string;
  'notificationChannel.authenticationConfig.clientId'?: string;
  'notificationChannel.authenticationConfig.clientSecret'?: string;
  'notificationChannel.authenticationConfig.clientScope'?: string;
  'notificationChannel.authenticationConfig.username'?: string;
  'notificationChannel.authenticationConfig.password'?: string;
}

interface IState {
  notificationChannel: NotificationChannelHttp;
  saving: boolean;
  deleteNotification: boolean;
  errors: IValidation;
  loading: boolean;
  showSecrets: boolean;
}

interface IProps
  extends WithStyles<typeof styles>,
    RouteComponentProps<INotificationRouteParams> {}

class NotificationChannelHttpDetail extends React.Component<IProps> {
  public state: IState = {
    notificationChannel: new NotificationChannelHttp(),
    saving: false,
    deleteNotification: false,
    errors: {},
    loading: false,
    showSecrets: false,
  };

  get notificationChannelId() {
    return this.props.match.params.notificationChannelId;
  }

  public async componentDidMount() {
    if (this.notificationChannelId) {
      this.setState({ loading: true });
      
      const notificationChannelFromApi = await NotificationChannelMgmtService.GetNotificationChannelHttpById(this.notificationChannelId);
      const notificationChannel: NotificationChannelHttp = this.mapToUiModel(notificationChannelFromApi);

      this.setState({
        notificationChannel,
        loading: false
      });
    }
  }

  public deleteNotificationChannelHttp = async () => {
    try {
      this.setState({ saving: true, deleteNotification: false });

      const httpCallConfig = new HttpCallConfig();
      httpCallConfig.successMessage = 'Notification Channel deleted';
      await NotificationChannelMgmtService.DeleteNotificationChannelHttp(
        this.notificationChannelId,
        httpCallConfig
      );

      this.props.history.push(notificationChannelsOverviewPath);
    } catch {
      this.setState({ saving: false });
    }
  };

  public mapToApiModel(notificationChannel: NotificationChannelHttp) {
    // API expects headers as dictionary<string, string> but for EditableList we need objects so we have [{key, value}]
    notificationChannel.customHeaders = {};
    notificationChannel.customHeadersList.forEach(header => {
      notificationChannel.customHeaders[header.key] = header.value;
    })
    notificationChannel.customHeadersList = [];
    
    if (notificationChannel.authenticationConfig) {
      notificationChannel.authenticationConfig.customHeaders = {};
      notificationChannel.authenticationConfig.customHeadersList.forEach(header => {
        notificationChannel.authenticationConfig.customHeaders[header.key] = header.value;
      })
      notificationChannel.authenticationConfig.customHeadersList = [];
    }
    
    return notificationChannel;
  }

  public mapToUiModel(notificationChannel: NotificationChannelHttp): NotificationChannelHttp {
    notificationChannel.customHeadersList = Object.keys(notificationChannel.customHeaders).map(headerKey => ({
      key: headerKey,
      value: notificationChannel.customHeaders[headerKey]
    }));

    if (!notificationChannel.authenticationConfig) {
      notificationChannel.authenticationConfig = new NotificationChannelHttpAuthenticationConfig();
    }

    if (notificationChannel.authenticationConfig.customHeaders) {
      notificationChannel.authenticationConfig.customHeadersList = Object.keys(notificationChannel.authenticationConfig.customHeaders).map(headerKey => ({
        key: headerKey,
        value: notificationChannel.authenticationConfig.customHeaders[headerKey]
      }));
    }
    
    return notificationChannel;
  }
  
  public saveNotificationChannel = async () => {
    try {
      const errors = this.validateState();
      if (Object.keys(errors).length) {
        return this.setState({ errors });
      }

      this.setState({ saving: true });

      const notificationChannel = this.mapToApiModel(this.state.notificationChannel);
      
      if (this.notificationChannelId) {
        const httpCallConfig = new HttpCallConfig();
        httpCallConfig.successMessage = 'Notification Channel updated successfully';
        await NotificationChannelMgmtService.PutNotificationChannelHttp(
          this.notificationChannelId,
          notificationChannel,
          httpCallConfig
        );
      } else {
        const httpCallConfig = new HttpCallConfig();
        httpCallConfig.successMessage = 'Notification Channel added successfully.';
        await NotificationChannelMgmtService.PostNotificationChannelHttp(
          notificationChannel,
          httpCallConfig
        );
      }

      this.props.history.push(notificationChannelsOverviewPath);
    } catch {
      this.setState({ saving: false });
    }
  };

  public onChannelTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const channelType = e.target.value.toLocaleLowerCase();
    const path = `/organization/notificationchannels/${channelType}/${this.notificationChannelId || 'add'}`;
    this.props.history.push(path);
  };
  
  // generic onChange 
  public getOnChange = (path: string) => {
    return (event: React.ChangeEvent<any>) => {
      const value =
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value;
      this.setState(state => StateHelper.ImmutableChange(state, path, value));
    };
  };

  public getGrantType(authenticationType: string) {
    if (authenticationType === NotificationChannelHttpAuthenticationType.OAUTH2CLIENTCREDENTIALS.id) {
      return "client_credentials";
    }
    if (authenticationType === NotificationChannelHttpAuthenticationType.OAUTH2PASSWORDFLOW.id) {
      return "password";
    }
    return "";
  }

  public onChangeAuthenticationType = () => {
    return (event: React.ChangeEvent<any>) => {
      const authenticationType = event.target.value;
      const grantType = this.getGrantType(authenticationType);

      this.setState({
        notificationChannel: {
          ...this.state.notificationChannel,
          authenticationType,
          authenticationConfig: {
            ...this.state.notificationChannel.authenticationConfig,
            grantType
          }
        }
      })
    };
  };
  
  public onChangeCustomHeaders = (updatedValues: any) => {
    this.setState({
      notificationChannel: {
        ...this.state.notificationChannel,
        customHeadersList: updatedValues
      }
    })
  };
  
  public onChangeCustomAuthenticationHeaders = (updatedValues: any) => {
    this.setState({
      notificationChannel: {
        ...this.state.notificationChannel,
        authenticationConfig: {
          ...this.state.notificationChannel.authenticationConfig,
          customHeadersList: updatedValues
        }
      }
    })
  };
  
  public onChangeShowSecrets = () => {
    this.setState({
      showSecrets: !this.state.showSecrets
    })
  }

  public validateState = () => {
    const { notificationChannel } = this.state;
    const errors: IValidation = {};

    if (!notificationChannel.name) {
      errors['notificationChannel.name'] = 'Name is required';
    }

    if (!IsValidHTTPSUrl(notificationChannel.address)) {
      errors['notificationChannel.address'] = 'Enter a valid HTTPS URL';
    }

    this.validateAuthenticationFields(notificationChannel, errors);
    this.validatePasswordFlowSpecificFields(notificationChannel, errors);

    return errors;
  };


  public validateAuthenticationFields(notificationChannel: NotificationChannelHttp, errors: IValidation) {
    if (notificationChannel.authenticationType !== NotificationChannelHttpAuthenticationType.NONE.id) {
      if (!IsValidHTTPSUrl(notificationChannel.authenticationConfig.tokenEndpoint)) {
        errors['notificationChannel.authenticationConfig.tokenEndpoint'] = 'Enter a valid HTTPS URL';
      }
      if (!notificationChannel.authenticationConfig.clientId) {
        errors['notificationChannel.authenticationConfig.clientId'] = 'Client Id is required';
      }
      if (!notificationChannel.authenticationConfig.clientSecret) {
        errors['notificationChannel.authenticationConfig.clientSecret'] = 'Client Secret is required';
      }

      if (notificationChannel.authenticationConfig.clientScope && notificationChannel.authenticationConfig.clientScope.includes(',')) {
        errors['notificationChannel.authenticationConfig.clientScope'] = 'Scopes should be separated by spaces';
      }
    }
  }

  public validatePasswordFlowSpecificFields(notificationChannel: NotificationChannelHttp, errors: IValidation) {
    if (notificationChannel.authenticationType === NotificationChannelHttpAuthenticationType.OAUTH2PASSWORDFLOW.id) {
      if (!notificationChannel.authenticationConfig.username) {
        errors['notificationChannel.authenticationConfig.username'] = 'Username is required';
      }
      if (!notificationChannel.authenticationConfig.password) {
        errors['notificationChannel.authenticationConfig.password'] = 'Password is required';
      }
    }
  }

  

  public render() {
    const { classes, history } = this.props;
    const {
      notificationChannel,
      saving,
      deleteNotification,
      errors,
      loading,
    } = this.state;


    if (loading) {
      return (
        <div>
          <Loading />
        </div>
      );
    }
    
    return (
      <Paper className={classes.paper}>
        { this.notificationChannelId &&
          <div className="padding-bottom-24">
            <Chip className="full-width" label="Due to security reasons, we do not visualize passwords and/or secrets. To make updates to these settings, these values need to be entered again." />
          </div>
        }

        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <FormControl>
              <InputLabel id="channelType">Channel Type</InputLabel>
              <Select
                id="channelType"
                disabled={true}
                value={notificationChannel.channelTypeId}
                onChange={this.onChannelTypeChange}
                className={classes.dropDown}
                style={{ width: 250}}
              >
                {Object.values(NotificationChannelTypeId).map(channelType => (
                  <MenuItem key={channelType.id} value={channelType.id}>
                    {channelType.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item={true}>
            <FormControl>
              <InputLabel id="notificationChannel.httpMethod">
                HTTP Method
              </InputLabel>
              <Select
                id="notificationChannel.httpMethod"
                disabled={true}
                value={notificationChannel.httpMethod}
                onChange={this.getOnChange('notificationChannel.httpMethod')}
                className={classes.dropDown}
              >
                {Object.values(NotificationChannelHttpMethod).map(httpMethod => (
                    <MenuItem key={httpMethod} value={httpMethod}>
                      {httpMethod}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
          
          
          {notificationChannel.id && (
            <Grid item={true}>
              <Button
                size="small"
                onClick={() => this.setState({ deleteNotification: true })}
                disabled={saving}
              >
                Remove Notification Channel
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <TextField
              name="notificationChannel.name"
              label="Channel Name"
              value={notificationChannel.name || ''}
              margin="normal"
              className={classes.textField}
              required={true}
              onChange={this.getOnChange('notificationChannel.name')}
              error={!!errors['notificationChannel.name']}
              helperText={errors['notificationChannel.name']}
            />
          </Grid>
        </Grid>
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <TextField
              name="notificationChannel.address"
              label="Webhook URL"
              value={notificationChannel.address || ''}
              margin="normal"
              className={classes.textField}
              required={true}
              onChange={this.getOnChange('notificationChannel.address')}
              error={!!errors['notificationChannel.address']}
              helperText={errors['notificationChannel.address']}
            />
          </Grid>
        </Grid>

        <Grid container={true} className={classes.editableList}>
          <Grid item={true} xs={4}>
            <Typography color="textSecondary" gutterBottom={true}>
              Headers
            </Typography>

            <EditableList
              editable
              directlyEditable
              headers={
                [
                  "Key",
                  "Value"
                ]
              }
              items={this.state.notificationChannel.customHeadersList}
              newItem={{
                key: '',
                value: ''
              }}
              settings={{
                key: {
                  type: 'text'
                },
                value: {
                  type: 'text'
                }
              }}
              onChange={this.onChangeCustomHeaders}
            />
          </Grid>
        </Grid>
        
        <Grid container={true} spacing={24}>
          <Grid item={true}>
              <FormControl>
                <InputLabel id="notificationChannel.authenticationType">
                  Authentication Type
                </InputLabel>
                <Select
                  id="notificationChannel.authenticationType"
                  value={notificationChannel.authenticationType}
                  onChange={this.onChangeAuthenticationType()}
                  className={classes.dropDown}
                  style={{ width: 250}}
                >
                  {Object.values(NotificationChannelHttpAuthenticationType).map(authenticationType => (
                      <MenuItem key={authenticationType.id} value={authenticationType.id}>
                        {authenticationType.description}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        
        <NotificationChannelHttpAuthenticationSettings
            notificationChannel={this.state.notificationChannel}
            showSecrets={this.state.showSecrets}
            classes={classes}
            getOnChange={this.getOnChange}
            onChangeHeaders={this.onChangeCustomAuthenticationHeaders}
            onChangeShowSecrets={this.onChangeShowSecrets}
            errors={this.state.errors}
        />
        
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.button}
              onClick={history.goBack}
            >
              BACK
            </Button>
          </Grid>
          <Grid item={true}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.button}
              onClick={this.saveNotificationChannel}
              disabled={saving}
            >
              SAVE
            </Button>
          </Grid>
        </Grid>
        <AreYouSureModal
          onClose={() => this.setState({ deleteNotification: false })}
          onConfirm={this.deleteNotificationChannelHttp}
          message="Are you sure you wish to remove this notification channel?"
          open={deleteNotification}
        />
      </Paper>
    );
  }
}

export default withRouter(withStyles(styles)(NotificationChannelHttpDetail));



// WEBPACK FOOTER //
// ./src/modules/organization/components/NotificationChannelHttpDetail.tsx